import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import css from './WaitingRoom.css';
import Header from './Header';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import Column from './Column';
import moment from 'moment';
import useInterval from 'use-interval';
import useStarfishAPI from "../hooks/useStarfishAPI";
import { urlParams } from "../utils/request";
import ConfirmDialog from 'common/ConfirmDialog';
import Toast from 'common/Toast';
import {isNotTimeoutError, isUpcomingAppointment} from "../utils/waitingRoom";
import { getWord } from 'utils/tenant';

const getDialogMessage = (isAppointment) =>
  isAppointment ? {
      headerText: "Unable to process this meeting.",
      descriptionText: "You don't have access to process this student."
    } : {
      headerText: "This student is scheduled to meet with another provider.",
      descriptionText: "You cannot start a meeting with them."
  };

function WaitingRoom(props) {
  const [studentsData, setStudentsData] = useState([]);
  const [zombiesData, setZombiesData] = useState([]);    
  const [appointmentsData, setAppointmentsData] = useState([]);
  const [services, setServices] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [dialogHeaderText, setDialogHeaderText] = useState("");
  const [dialogDescriptionText, setDialogDescriptionText] = useState("");

  // API calls
  const [studentsResponse, callStudentsAPI] = useStarfishAPI();
  const [zombiesResponse, callZombiesAPI] = useStarfishAPI();
  const [appointmentsResponse, callAppointmentsAPI] = useStarfishAPI();

  const getWaitingRoomStudents = () => {
    const URL = `../serviceCatalog/getWaitingRoomStudents.html`;
    callStudentsAPI(URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: urlParams({ serviceId: services.map(service => service.id).join(",") })
    });
  };

  const getWaitingRoomZombies = () => {
    const URL = `../serviceCatalog/getWaitingRoomZombies.html`;
    callZombiesAPI(URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: urlParams({ serviceId: services.map(service => service.id).join(",") })
    });
  };

  const getWaitingRoomAppointments = () => {
    const URL = `../serviceCatalog/getWaitingRoomAppointments.html`;
    callAppointmentsAPI(URL, { method: 'GET' });
  };

  // Whenever API changes, set state variable again
  useEffect(() => {
    if ( studentsResponse.success ) setStudentsData(studentsResponse.data.results);
  }, [studentsResponse]);

  useEffect(() => {
    if ( zombiesResponse.success ) setZombiesData(zombiesResponse.data.results);
  }, [zombiesResponse]);

  useEffect(() => {
    if ( appointmentsResponse.success ) setAppointmentsData(appointmentsResponse.data.results);
  }, [appointmentsResponse]);

  // Has any zombie student, i.e., student being processed in the walkin queue OR
  // has an appointment which is a group session and is currently under "PROCESSING" status
  const hasStudentsInProgress = () => (
    zombiesData.length ||
    appointmentsData.filter(({groupSession, status}) => groupSession && status === "PROCESSING").length > 0
  );

  useEffect(() => {
    props.setWarning(hasStudentsInProgress()
      ? "One of your students was not processed out of the Waiting Room. Please resume the meeting."
      : ""
    );
  }, [zombiesData, appointmentsData]);

  const refreshData = () => {
    getWaitingRoomStudents();
    getWaitingRoomZombies();
    getWaitingRoomAppointments();
  };

  // Whenever we change services, fetch API again. Make sure to not run when the component
  // first mounts, so we avoid fetching unnecessarily without any data
  const hasFetchedServices = useRef(false);
  useEffect(() => {
    if ( !hasFetchedServices.current ) hasFetchedServices.current = true;
    else refreshData();
  }, [services])
  
  // Fetch API again every minute
  useInterval(() => {
    refreshData();
  }, 60 * 1000);

  // Add error listeners for every response
  useEffect(() => {
    if (isNotTimeoutError(studentsResponse) ) {
      props.setError(studentsResponse.error);
    } else if (isNotTimeoutError(zombiesResponse)) {
      props.setError(zombiesResponse.error);
    } else if (isNotTimeoutError(appointmentsResponse)) {
      props.setError(appointmentsResponse.error);
    }
  }, [studentsResponse, zombiesResponse, appointmentsResponse]);

  // Students who have checked in as walk in. This will come from getWaitingRoomStudents.html
  const getWalkInQueue = () => studentsData.filter(({appointment, status}) => !appointment && status === "WAITING");    

  // Walk in only students who are currently in a meeting. This will come from getWaitingRoomZombies.html
  const getWalkInProgress = () => zombiesData.filter(({appointment}) => !appointment);

  // Scheduled students for the day who aren't currently checked in. 
  // This will come from getWaitingRoomAppointments.html
  const getAppointmentsScheduled = () => appointmentsData
    .filter(appointment => isUpcomingAppointment(appointment))
    .sort((a, b) => a.startDate < b.startDate ? -1 : 1);

  // Scheduled students who have already checked in. This will come from getWaitingRoomStudents.html
  const getAppointmentsCheckedIn = () => studentsData
    .filter(({appointment, status}) => appointment && status === "WAITING")
    .concat(zombiesData.filter(({appointment}) => appointment))
    .concat(appointmentsData.filter(({groupSession, status, participants}) => groupSession
      && (participants.some(participant => participant.waiting) || status === "PROCESSING")
    ))
    .sort((a, b) => {
      const getStartDate = item => item.startDate || (item.appointment && item.appointment.startDate);
      return getStartDate(a) < getStartDate(b) ? -1 : 1;
    });

  const walkInCount = getWalkInQueue().length + getWalkInProgress().length;
  const appointmentsCount = getAppointmentsScheduled().length + getAppointmentsCheckedIn().length;
  const lastUpdatedDisplay = studentsResponse.success ? studentsResponse.data.lastUpdatedDisplay : '';

  const openDialog = (isAppointment) => {
    setIsDialogOpen(true);
    const dialogMessage = getDialogMessage(isAppointment);
    setDialogHeaderText(dialogMessage.headerText);
    setDialogDescriptionText(dialogMessage.descriptionText);
  };

  // Calls ExtJS function. Passes a refreshData function which is called on success and
  // openDialog function (when applicable), which is called if student is already being processed
  const onStartMeeting = item => props.startMeeting(null, item, refreshData, openDialog);
  const onRemoveFromQueue = item => props.removeFromQueue(null, item, refreshData);
  const onEditDescription = item => props.editDescription(null, item, refreshData, setToastMessage);
  const onEditAppointment = item => props.editAppointment(null, item, getWaitingRoomAppointments);
  const onEditGroupSession = item => props.handleGroupSession(null, item, 'Manage Participants', getWaitingRoomAppointments);
  const onStartGroupSession = item => props.handleGroupSession(null, item, 'Start Group Session', refreshData);

  return (
    <>
      <Header
        services={services}
        setServices={setServices}
        setError={props.setError}
        lastUpdatedDisplay={lastUpdatedDisplay}
        studentsResponseData={studentsResponse.data}
      />
      <Tabs
        className={css['container']}
        selectedTabClassName={css['tab-selected']}
        selectedTabPanelClassName={css['panel']}>
        <TabList className={css['list-container']}>
          <Tab data-role="walkInsTab">{getWord('dropIns', false)} ({walkInCount})</Tab>
          <Tab data-role="myAppointmentsTab">Appointments ({appointmentsCount})</Tab>
        </TabList>
          <TabPanel>
            <Column 
              title="Queue"
              columnName="queueColumn"
              emptyText="No students in the queue."
              subTitleMask="# student(s) in the queue."
              items={getWalkInQueue()}
              services={services}
              onRemoveFromQueue={onRemoveFromQueue}
              onStartMeeting={onStartMeeting}
              onEditDescription={onEditDescription}
              openStudentFolder={props.openStudentFolder}
            />
            <Column 
              title="In Progress" 
              columnName="inProgressColumn"
              emptyText="No meetings in progress."
              items={getWalkInProgress()}
              services={services}
              onRemoveFromQueue={onRemoveFromQueue}
              onStartMeeting={onStartMeeting}
              onEditDescription={onEditDescription}
              openStudentFolder={props.openStudentFolder}
            />
          </TabPanel>
          <TabPanel>
            <Column 
              title={`My Appointments for ${moment().format("M/D/YY")}`}
              columnName="upcomingAppointments"
              emptyText="No scheduled appointments."
              subTitleMask="You have # upcoming appointment(s)."
              items={getAppointmentsScheduled()}
              services={services}
              onEditAppointment={onEditAppointment}
              onEditGroupSession={onEditGroupSession}
              openStudentFolder={props.openStudentFolder}
            />
            <Column 
              title="Checked In"
              columnName="appointmentsCheckedIn"
              emptyText="No students checked in for scheduled appointments."
              subTitleMask={"This list includes students scheduled with other providers."}
              items={getAppointmentsCheckedIn()}
              services={services}
              onRemoveFromQueue={onRemoveFromQueue}
              onStartMeeting={onStartMeeting}
              onStartGroupSession={onStartGroupSession}
              onEditGroupSession={onEditGroupSession}
              openStudentFolder={props.openStudentFolder}
            />
          </TabPanel>
      </Tabs>
      <ConfirmDialog
        isOpen={isDialogOpen}
        headerText={dialogHeaderText}
        descriptionText={dialogDescriptionText}
        confirmText="Okay"
        onConfirm={() => setIsDialogOpen(false)}
      />
      {toastMessage && <Toast 
        message={toastMessage} 
        onTimeout={() => setToastMessage("")} 
      />}
    </>
  );
}

WaitingRoom.propTypes = {
  setError: PropTypes.func.isRequired,
  setWarning: PropTypes.func.isRequired,
  removeFromQueue: PropTypes.func.isRequired,
  startMeeting: PropTypes.func.isRequired,
  editDescription: PropTypes.func.isRequired,
  editAppointment: PropTypes.func.isRequired,
  handleGroupSession: PropTypes.func.isRequired,
  openStudentFolder: PropTypes.func.isRequired
};

export default WaitingRoom;
